import { DeprecatedGrid as Grid } from '@simplywallst/ui-core';

import { formatterFactor, formatterPercentage } from '@/utilities/format';

import { Metric } from './Metric';
import { FadedNil } from './styled';

const resolvePreferredMultiple = (multiple: string | null) => {
  if (multiple && ['pe', 'ps', 'pb'].includes(multiple)) {
    return multiple;
  }

  return 'pb';
};

interface Props {
  priceToSales: number | null;
  priceToBook: number | null;
  priceToEarnings: number | null;
  preferredRelativeMultiple: string | null;
  dividendYield: number | null;
  forwardPe1Y: number | null;
  forwardPriceToSales1Y: number | null;
  salesGrowth: number | null;
  earningsGrowth: number | null;
}

export const ValuationMetrics = ({
  priceToSales,
  priceToBook,
  priceToEarnings,
  preferredRelativeMultiple,
  dividendYield,
  forwardPe1Y,
  forwardPriceToSales1Y,
  salesGrowth,
  earningsGrowth,
}: Props) => {
  const preferredMultiple = resolvePreferredMultiple(preferredRelativeMultiple);

  return (
    <Grid
      as="dl"
      data-cy-id="valuation-metrics"
      templateColumns="repeat(4, 1fr)"
      gap="12px"
    >
      {preferredMultiple === 'pe' ? (
        <>
          <Metric
            data-cy-id="pe-metric"
            term="PE"
            detail={
              <FadedNil value={priceToEarnings}>
                {formatterFactor(priceToEarnings)}
              </FadedNil>
            }
          />
          <Metric
            data-cy-id="forward-pe-metric"
            term="Fwd PE"
            detail={
              <FadedNil value={forwardPe1Y}>
                {formatterFactor(forwardPe1Y)}
              </FadedNil>
            }
          />
          <Metric
            data-cy-id="earnings-growth-metric"
            term="E. Growth"
            detail={
              <FadedNil value={earningsGrowth}>
                {formatterPercentage(earningsGrowth)}
              </FadedNil>
            }
          />
        </>
      ) : null}
      {preferredMultiple === 'ps' ? (
        <>
          <Metric
            data-cy-id="ps-metric"
            term="PS"
            detail={
              <FadedNil value={priceToSales}>
                {formatterFactor(priceToSales)}
              </FadedNil>
            }
          />
          <Metric
            data-cy-id="forward-ps-metric"
            term="Fwd PS"
            detail={
              <FadedNil value={forwardPriceToSales1Y}>
                {formatterFactor(forwardPriceToSales1Y)}
              </FadedNil>
            }
          />
          <Metric
            data-cy-id="sales-growth-metric"
            term="Sales Growth"
            detail={
              <FadedNil value={salesGrowth}>
                {formatterPercentage(salesGrowth)}
              </FadedNil>
            }
          />
        </>
      ) : null}
      {preferredMultiple === 'pb' ? (
        <>
          <Metric
            data-cy-id="pb-metric"
            term="PB"
            detail={
              <FadedNil value={priceToBook}>
                {formatterFactor(priceToBook)}
              </FadedNil>
            }
          />
          <Metric
            data-cy-id="forward-pb-metric"
            term="Fwd PB"
            detail={<FadedNil value={null}>{formatterFactor(null)}</FadedNil>}
          />
          <Metric
            data-cy-id="earnings-growth-metric"
            term="E. Growth"
            detail={
              <FadedNil value={earningsGrowth}>
                {formatterPercentage(earningsGrowth)}
              </FadedNil>
            }
          />
        </>
      ) : null}
      <Metric
        data-cy-id="dividend-yield-metric"
        term="Div. Yield"
        detail={
          <FadedNil value={dividendYield}>
            {formatterPercentage(dividendYield)}
          </FadedNil>
        }
      />
    </Grid>
  );
};
